import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Header from '../../../components/feature/Header';
import Footer from '../../../components/feature/Footer';

export default function KommetjiePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
    
    if (typeof document === 'undefined') return;

    try {
      document.title = 'Kommetjie Painters Cape Town | Coastal Village Painting Services | Beach Properties';

      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription) {
        metaDescription.setAttribute('content', 'Expert painting services in Kommetjie, Cape Town. Specialist coastal painting for beach houses, surf village homes, and seaside properties. Marine-grade protection and beautiful finishes.');
      }

      const metaKeywords = document.querySelector('meta[name="keywords"]');
      if (metaKeywords) {
        metaKeywords.setAttribute('content', 'Kommetjie painters, coastal painting Cape Town, beach house painting');
      }

      const canonical = document.querySelector('link[rel="canonical"]');
      if (canonical) {
        canonical.setAttribute('href', `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/areas/kommetjie`);
      }
    } catch (err) {
      console.error('Error setting up meta tags:', err);
    }
  }, []);

  return (
    <div className="min-h-screen">
      <Header />

      {/* Hero Section */}
      <section className="relative bg-gradient-to-br from-gray-900 to-black text-white py-32 min-h-[600px]">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/b4ac2643f18b9e1eb40ec66acfd5ee3b.jpg"
            alt="Professional Painting Services in Kommetjie"
            className="w-full h-full object-cover"
            loading="eager"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/60 via-black/50 to-black/70"></div>
        </div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl lg:text-6xl font-bold leading-tight mb-6">
              Professional <span className="text-yellow-400">Kommetjie Painters</span>
            </h1>
            <p className="text-xl text-gray-300 leading-relaxed mb-8">
              Expert coastal painting for Kommetjie's beach houses, surf village homes, and seaside properties with marine-grade protection and beautiful finishes.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link to="/contact" className="bg-yellow-400 text-black px-8 py-4 rounded-lg text-xl font-bold hover:bg-yellow-500 transition-colors whitespace-nowrap cursor-pointer">
                Get Free Quote
              </Link>
              <Link to="/services" className="bg-white text-black px-8 py-4 rounded-lg text-lg font-semibold hover:bg-gray-100 transition-colors text-center whitespace-nowrap cursor-pointer border border-gray-200">
                Our Services
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Introduction Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold mb-6 text-gray-900">
              Specialized Painting for Kommetjie's Unique Coastal Character
            </h2>
            <div className="prose prose-lg max-w-none text-gray-700 space-y-4">
              <p>
                Kommetjie stands as one of Cape Town's most beloved coastal villages, renowned for its laid-back surf culture, stunning Atlantic Ocean beaches, iconic lighthouse, and relaxed seaside atmosphere. This unique community requires painting services that understand and celebrate its distinctive beach village character while providing robust protection against the challenging marine environment that defines this special False Bay and Atlantic Ocean location.
              </p>
              <p>
                Our specialized painting services in Kommetjie honor the area's coastal heritage and surf culture while delivering modern protection and durability. We work with homeowners, holiday property owners, and local businesses to enhance the village's relaxed aesthetic, protect properties from salt air and ocean spray, and maintain the authentic beach house character that makes Kommetjie such a desirable coastal destination.
              </p>
              <p>
                Working in Kommetjie requires understanding of coastal painting challenges, appreciation for the community's surf culture, and respect for the village's environmental sensitivity. Our experienced team has completed numerous projects throughout this coastal village, from beach houses and surf cottages to holiday homes and local businesses, earning trust through quality workmanship and respect for Kommetjie's unique identity and natural beauty.
              </p>
              <p>
                From vibrant beach house exteriors to cozy cottage interiors and commercial property maintenance, we provide comprehensive painting solutions that enhance Kommetjie's charm, protect valuable coastal investments, and contribute to the village's distinctive visual appeal. Our commitment to excellence ensures every property receives expert care that balances aesthetic beauty with practical durability in this special coastal environment where ocean meets community.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Our Kommetjie Painting Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Comprehensive coastal painting solutions celebrating Kommetjie's beach village character
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-home-4-line',
                title: 'Beach House Painting',
                description: 'Expert painting for Kommetjie beach houses with coastal colors, marine-grade protection, and finishes that capture the relaxed surf village atmosphere.',
                features: ['Coastal color palettes', 'Marine-grade coatings', 'Salt air protection', 'Beach house aesthetics', 'Weather resistance', 'Relaxed finishes']
              },
              {
                icon: 'ri-shield-star-line',
                title: 'Coastal Protection Systems',
                description: 'Advanced marine-grade exterior painting systems designed specifically for Kommetjie\'s harsh Atlantic and False Bay coastal environment.',
                features: ['Salt-resistant coatings', 'Ocean spray protection', 'UV-stable pigments', 'Moisture management', 'Wind resistance', 'Long-term durability']
              },
              {
                icon: 'ri-palette-line',
                title: 'Surf Village Exteriors',
                description: 'Vibrant exterior painting celebrating Kommetjie\'s surf culture with beach-inspired colors and weather-resistant finishes that maintain the village vibe.',
                features: ['Surf-inspired colors', 'Fade-resistant finishes', 'Creative consultation', 'Village harmony', 'Beach aesthetics', 'Color retention']
              },
              {
                icon: 'ri-home-heart-line',
                title: 'Holiday Home Painting',
                description: 'Professional painting for holiday properties and vacation homes with durable finishes that withstand coastal conditions and minimal maintenance requirements.',
                features: ['Low-maintenance finishes', 'Durable coatings', 'Holiday home expertise', 'Coastal protection', 'Beautiful results', 'Long-lasting quality']
              },
              {
                icon: 'ri-store-2-line',
                title: 'Local Business Painting',
                description: 'Expert painting for Kommetjie cafes, surf shops, and local businesses with attractive finishes that draw customers and reflect the coastal lifestyle.',
                features: ['Business-friendly scheduling', 'Attractive finishes', 'Coastal aesthetics', 'Quick turnaround', 'Minimal disruption', 'Customer appeal']
              },
              {
                icon: 'ri-tools-line',
                title: 'Coastal Maintenance Programs',
                description: 'Ongoing maintenance services for Kommetjie properties with regular inspections, preventive care, and touch-ups to combat harsh coastal wear.',
                features: ['Regular maintenance', 'Coastal wear prevention', 'Priority service', 'Preventive care', 'Touch-up programs', 'Long-term protection']
              }
            ].map((service, index) => (
              <div key={index} className="bg-white rounded-lg p-8 shadow-lg hover:shadow-xl transition-all cursor-pointer">
                <div className="w-16 h-16 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className={`${service.icon} text-3xl text-black`}></i>
                </div>
                <h3 className="text-2xl font-bold mb-4 text-gray-900">{service.title}</h3>
                <p className="text-gray-700 mb-6 leading-relaxed">{service.description}</p>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-start">
                      <i className="ri-check-line text-yellow-400 text-xl mr-2 flex-shrink-0"></i>
                      <span className="text-gray-700">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Challenges Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">
            Understanding Kommetjie's Coastal Painting Challenges
          </h2>
          <div className="grid md:grid-cols-2 gap-12 max-w-6xl mx-auto">
            <div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">Extreme Coastal Exposure</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Kommetjie's position on the Atlantic Ocean and False Bay coastline exposes properties to some of the harshest marine conditions in Cape Town including constant salt spray, strong coastal winds, high humidity, and direct ocean exposure. Our marine-grade painting systems utilize specialized salt-resistant primers, breathable topcoats, and advanced protective treatments that defend surfaces from coastal aggression.
              </p>
              <p className="text-gray-700 leading-relaxed">
                We understand the unique challenges of painting in an exposed coastal village and employ advanced coating technologies, meticulous surface preparation, and strategic application timing that ensures maximum durability and longevity despite Kommetjie's demanding maritime environment and weather patterns.
              </p>
            </div>
            <div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">Beach Village Character</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Kommetjie's relaxed surf culture and beach village atmosphere require painting approaches that enhance rather than compromise the area's authentic coastal character. Our color consultation and finishing techniques celebrate the village's laid-back aesthetic while ensuring properties receive professional-quality protection and beautiful results that fit naturally within the community.
              </p>
              <p className="text-gray-700 leading-relaxed">
                We work with property owners to select colors and finishes that express individual style while maintaining harmony with Kommetjie's coastal environment and surf village vibe. Our approach balances creative expression with practical durability, ensuring properties look great and withstand coastal conditions.
              </p>
            </div>
            <div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">Wind and Weather</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Kommetjie experiences strong prevailing winds, particularly the south-easter during summer months, which can accelerate paint deterioration and surface damage. Our weather-resistant coating systems are specifically designed to withstand wind-driven rain, sand abrasion, and temperature fluctuations that characterize this exposed coastal location.
              </p>
              <p className="text-gray-700 leading-relaxed">
                We select premium materials with superior adhesion, flexibility, and weather resistance that perform reliably in Kommetjie's challenging conditions. Our application techniques account for wind patterns and weather windows to ensure optimal curing and long-term performance of protective coatings.
              </p>
            </div>
            <div>
              <h3 className="text-2xl font-bold mb-4 text-gray-900">Environmental Sensitivity</h3>
              <p className="text-gray-700 leading-relaxed mb-4">
                Kommetjie's natural beauty and environmental significance require responsible painting practices that minimize environmental impact. We utilize eco-friendly materials when appropriate, implement proper waste management, and respect the area's natural surroundings while delivering professional painting services.
              </p>
              <p className="text-gray-700 leading-relaxed">
                Our environmentally conscious approach includes careful site management, protection of natural areas, and use of low-VOC materials where suitable. We balance environmental responsibility with performance requirements to deliver sustainable painting solutions for Kommetjie's coastal properties.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Our Kommetjie Painting Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              A systematic approach ensuring exceptional results for coastal village properties
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                step: '01',
                title: 'Coastal Property Assessment',
                description: 'Thorough on-site evaluation of your Kommetjie property including ocean exposure assessment, environmental factors, and detailed discussion of your vision and requirements.'
              },
              {
                step: '02',
                title: 'Beach-Inspired Design',
                description: 'Expert color consultation celebrating Kommetjie\'s coastal character with recommendations for weather-resistant colors and finishes that enhance your beach property.'
              },
              {
                step: '03',
                title: 'Detailed Proposal',
                description: 'Comprehensive quotation outlining scope, specialized marine-grade materials, timeline, and transparent pricing with no hidden costs throughout your project.'
              },
              {
                step: '04',
                title: 'Marine-Grade Preparation',
                description: 'Meticulous preparation including salt removal, surface repairs, moisture treatment, and proper priming to ensure optimal adhesion in harsh coastal conditions.'
              },
              {
                step: '05',
                title: 'Expert Coastal Application',
                description: 'Professional painting using marine-grade materials, proven coastal techniques, and experienced craftsmen who understand Kommetjie\'s unique environmental challenges.'
              },
              {
                step: '06',
                title: 'Quality Inspection',
                description: 'Thorough final inspection, detailed touch-ups, and complete walkthrough ensuring every aspect meets our exacting standards and your complete satisfaction.'
              }
            ].map((item, index) => (
              <div key={index} className="bg-white rounded-lg p-8 shadow-lg hover:shadow-xl transition-all">
                <div className="text-5xl font-bold text-yellow-400 mb-4">{item.step}</div>
                <h3 className="text-2xl font-bold mb-4 text-gray-900">{item.title}</h3>
                <p className="text-gray-700 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">
            Why Kommetjie Property Owners Choose Us
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              {
                icon: 'ri-ship-line',
                title: 'Coastal Expertise',
                description: 'Specialized knowledge and extensive experience in extreme coastal painting challenges, marine-grade materials, and proven techniques that protect Kommetjie properties from harsh ocean conditions.'
              },
              {
                icon: 'ri-palette-line',
                title: 'Beach Village Aesthetic',
                description: 'Deep appreciation for Kommetjie\'s surf culture and coastal character with color expertise that celebrates the village vibe while ensuring fade-resistant, long-lasting results.'
              },
              {
                icon: 'ri-shield-check-line',
                title: 'Marine-Grade Protection',
                description: 'Advanced coating systems specifically designed for extreme coastal exposure providing superior protection against salt air, ocean spray, wind, and weather unique to Kommetjie.'
              },
              {
                icon: 'ri-community-line',
                title: 'Local Understanding',
                description: 'Familiarity with Kommetjie\'s unique community, environmental sensitivity, and coastal lifestyle with respectful work practices that honor the area\'s special character and natural beauty.'
              },
              {
                icon: 'ri-leaf-line',
                title: 'Environmental Responsibility',
                description: 'Commitment to environmentally conscious practices using eco-friendly materials when appropriate and implementing responsible site management that respects Kommetjie\'s natural surroundings.'
              },
              {
                icon: 'ri-customer-service-line',
                title: 'Professional Service',
                description: 'Reliable, courteous, and professional service from initial consultation through project completion with clear communication, respect for your property, and dedication to your satisfaction.'
              }
            ].map((item, index) => (
              <div key={index} className="text-center">
                <div className="w-16 h-16 bg-yellow-400 rounded-full flex items-center justify-center mx-auto mb-6">
                  <i className={`${item.icon} text-3xl text-black`}></i>
                </div>
                <h3 className="text-2xl font-bold mb-4 text-gray-900">{item.title}</h3>
                <p className="text-gray-700 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Recent Projects Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-gray-900 mb-6">
              Recent Kommetjie Projects
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Showcase of our coastal painting projects celebrating Kommetjie's beach village character
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              {
                title: 'Oceanfront Beach House',
                location: 'Kommetjie Beachfront',
                description: 'Complete exterior and interior painting for a stunning oceanfront beach house featuring coastal color palette, marine-grade protective coatings, and finishes that capture the relaxed surf village atmosphere while providing superior protection.',
                image: 'https://readdy.ai/api/search-image?query=Oceanfront%20beach%20house%20in%20Kommetjie%20Cape%20Town%20with%20fresh%20coastal%20paint%2C%20ocean%20views%2C%20surf%20village%20atmosphere%2C%20professional%20finish%2C%20relaxed%20coastal%20living%2C%20beautiful%20exterior&width=800&height=600&seq=kommetjie-project-001&orientation=landscape'
              },
              {
                title: 'Surf Cottage Renovation',
                location: 'Kommetjie Village',
                description: 'Charming renovation of a classic surf cottage including vibrant exterior colors, weather-resistant coatings, and cozy interior finishes that enhanced the property\'s beach house character and coastal appeal.',
                image: 'https://readdy.ai/api/search-image?query=Charming%20surf%20cottage%20in%20Kommetjie%20Cape%20Town%20with%20colorful%20paint%2C%20beach%20house%20style%2C%20coastal%20village%20setting%2C%20professional%20renovation%2C%20relaxed%20atmosphere&width=800&height=600&seq=kommetjie-project-002&orientation=landscape'
              },
              {
                title: 'Holiday Home Transformation',
                location: 'Kommetjie Coastal Area',
                description: 'Professional painting for a holiday home including durable low-maintenance finishes, salt-resistant coatings, and beach-inspired colors that created an inviting coastal retreat requiring minimal upkeep.',
                image: 'https://readdy.ai/api/search-image?query=Holiday%20home%20in%20Kommetjie%20Cape%20Town%20with%20fresh%20paint%2C%20coastal%20colors%2C%20beach%20property%2C%20professional%20finish%2C%20vacation%20home%2C%20ocean%20proximity&width=800&height=600&seq=kommetjie-project-003&orientation=landscape'
              },
              {
                title: 'Local Cafe Painting',
                location: 'Kommetjie Main Road',
                description: 'Attractive exterior and interior painting for a popular local cafe including surf-inspired colors, durable finishes, and efficient scheduling that minimized business disruption while creating an inviting coastal atmosphere.',
                image: 'https://readdy.ai/api/search-image?query=Coastal%20cafe%20in%20Kommetjie%20Cape%20Town%20with%20attractive%20paint%2C%20surf%20village%20atmosphere%2C%20welcoming%20exterior%2C%20professional%20finish%2C%20local%20business%2C%20beach%20community&width=800&height=600&seq=kommetjie-project-004&orientation=landscape'
              }
            ].map((project, index) => (
              <div key={index} className="bg-white rounded-lg overflow-hidden shadow-lg hover:shadow-xl transition-all">
                <div className="w-full h-80">
                  <img src={project.image} alt={project.title} className="w-full h-full object-cover object-top" />
                </div>
                <div className="p-8">
                  <div className="flex items-center text-sm text-gray-500 mb-2">
                    <i className="ri-map-pin-line mr-2"></i>
                    {project.location}
                  </div>
                  <h3 className="text-2xl font-bold mb-4 text-gray-900">{project.title}</h3>
                  <p className="text-gray-700 leading-relaxed">{project.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6">
          <h2 className="text-4xl font-bold text-center mb-12 text-gray-900">
            What Kommetjie Clients Say
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                name: 'Mike & Lisa Anderson',
                location: 'Kommetjie Beach House',
                text: 'Exceptional coastal painting service! Our beach house looks amazing and the marine-grade coatings are holding up perfectly against the harsh ocean conditions. The team understood exactly what we needed for our Kommetjie property.'
              },
              {
                name: 'Tom Richards',
                location: 'Kommetjie Holiday Home',
                text: 'Professional work with beautiful results. The low-maintenance finishes are perfect for our holiday home, and the coastal colors capture the Kommetjie vibe perfectly. Highly recommend for any beach property!'
              },
              {
                name: 'Sarah du Plessis',
                location: 'Kommetjie Cafe Owner',
                text: 'Great service with minimal disruption to our business. The surf-inspired colors look fantastic and have attracted more customers. Perfect understanding of what works in Kommetjie\'s coastal environment.'
              }
            ].map((testimonial, index) => (
              <div key={index} className="bg-gray-50 rounded-lg p-8 shadow-lg">
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <i key={i} className="ri-star-fill text-yellow-400 text-xl"></i>
                  ))}
                </div>
                <p className="text-gray-700 mb-6 leading-relaxed italic">&quot;{testimonial.text}&quot;</p>
                <div>
                  <p className="font-bold text-gray-900">{testimonial.name}</p>
                  <p className="text-gray-600">{testimonial.location}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-yellow-400">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <h2 className="text-4xl font-bold text-black mb-6">
            Ready to Transform Your Kommetjie Property?
          </h2>
          <p className="text-xl text-black/80 mb-8 max-w-3xl mx-auto">
            Contact us today for a free consultation and discover how our coastal painting expertise can enhance your beach property's beauty and protection.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/contact" className="bg-black text-white px-10 py-4 rounded-lg text-xl font-bold hover:bg-gray-800 transition-colors whitespace-nowrap cursor-pointer">
              Get Free Quote
            </Link>
            <a href="tel:+27123456789" className="bg-white text-black px-8 py-4 rounded-lg text-xl font-bold hover:bg-gray-100 transition-colors whitespace-nowrap cursor-pointer">
              Call Now
            </a>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}